"use client"

import styled from "@emotion/styled"
import {
  FormControl,
  FormControlLabel,
  InputLabel,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material"
import {
  Int32StringValueTuple,
  LostFoundColorPublicResponse,
  LostFoundContentPublicResponse,
} from "api"
import { useTranslations } from "next-intl"
import Image from "next/image"
import { usePathname, useRouter } from "next/navigation"
import { useEffect, useState } from "react"
import { applicationRequestClick } from "utils"
import Loading from "../Common/Loading"
import { FormError } from "./ContactContent"
import Complaint from "./Forms/Complaint"
import LostFound from "./Forms/Lost&Found"
import LostStatusCheck from "./Forms/LostStatusCheck"
import OptimizationProposal from "./Forms/OptimizationProposal"
import Ravkav from "./Forms/Ravkav"
import PenaltyLookup from "./Forms/PenaltyLookup"
import RequestForCredit from "./Forms/RequestForCredit"
import RequestForTaxInvoice from "./Forms/RequestFotTaxInvoice"
import InformationBlocks from "./components/InformationBox"

const StyledLayout = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  ${({ theme }) => theme.breakpoints.up("md")} {
    gap: 48pxpx;
  }
`

const StyledFormControl = styled(FormControl)`
  width: 100%;
  ${({ theme }) => theme.breakpoints.up("md")} {
    width: 352px;
    margin-top: -8px;
  }
`

const StyledInputLabel = styled(InputLabel)`
  &[data-shrink="true"] {
    transform: translate(14px, 0px) scale(0.75);
  }
`

const StyledRadioGroup = styled(RadioGroup)`
  display: flex;
  gap: 24px;
  ${({ theme }) => theme.breakpoints.up("md")} {
    flex-direction: row;
  }
`

const StyledFormControlLabel = styled(FormControlLabel)`
  font-size: 18px;
  line-height: 26px;

  /* Focus styles for the entire label */
  &:focus-visible {
    outline: 2px solid black;
    outline-offset: 2px;
    border-radius: 4px;
  }

  /* Target the radio button container specifically */
  .MuiRadio-root:focus-visible {
    outline: 2px solid black !important;
    outline-offset: 2px !important;
    border-radius: 50% !important;
  }
`

const englishLabels = [
  "Complaint",
  "Comment, suggestion, inquiry",
  "Report a Lost Item",
  "Refund request - Rav-Kav",
  "Retrieve Penalties (penalty tariff)",
  "Request credit card refund",
  "Request tax invoice",
  "Lost Item Status",
]

const StyledRadio = styled(Radio)`
  /* Target the Image component wrapper when radio is focused */
  &:focus-visible img {
    outline: 2px solid black !important;
    outline-offset: 2px !important;
    border-radius: 50% !important;
  }

  /* Target when the radio input is focused */
  input:focus-visible + * img {
    outline: 2px solid black !important;
    outline-offset: 2px !important;
    border-radius: 50% !important;
  }

  /* Target the span containing the image when focused */
  &:focus-within img {
    outline: 2px solid black !important;
    outline-offset: 2px !important;
    border-radius: 50% !important;
  }

  /* Alternative approach - target all images in focused radio */
  &.Mui-focusVisible img {
    outline: 2px solid black !important;
    outline-offset: 2px !important;
    border-radius: 50% !important;
  }
`
export type FormId = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8

export default function Form({
  contactTypesData,
  handleResult,
  locale,
  initialFormType,
  lostFoundContentData,
  lostFoundColorsData,
  lostAndFoundStatus,
  contactUsData,
  tooltipText = "",
}: {
  contactTypesData: Int32StringValueTuple[]
  handleResult: (type: FormError, id?: string) => void
  locale: "he" | "en" | "ar" | "ru"
  initialFormType?: number | null
  lostFoundContentData: LostFoundContentPublicResponse[]
  lostFoundColorsData: LostFoundColorPublicResponse[]
  lostAndFoundStatus: any
  contactUsData: any
  tooltipText?: string
}) {
  const hebrew = locale === "he"
  // Adjust Hebrew label for the second contact type per request
  const adjustedContactTypesData = hebrew
    ? contactTypesData.map((ct, idx) =>
        idx === 1 ? { ...ct, item2: "פנייה, הצעה, שבח" } : ct,
      )
    : contactTypesData
  const [isLoading, setIsLoading] = useState(false)
  const tInner = useTranslations("InnerPages")
  const [subject, setSubject] = useState<FormId | "">(
    initialFormType ? (initialFormType as FormId) : "",
  )
  const router = useRouter()
  const pathname = usePathname()

  const isEmbedded = pathname?.startsWith("/embed") || false

  useEffect(() => {
    if (initialFormType) {
      setSubject(initialFormType as FormId)
    }
  }, [initialFormType])

  // Focus management when subject changes
  useEffect(() => {
    if (subject && !isLoading) {
      // Check if focus is currently within the radio group
      const activeElement = document.activeElement
      const radioGroup = document.querySelector('[role="radiogroup"]')
      const isRadioGroupFocused = radioGroup?.contains(activeElement)

      // Only move focus to form if not currently navigating within radio group
      if (!isRadioGroupFocused) {
        // Small delay to ensure the form is rendered
        const timerId = setTimeout(() => {
          // Find the first input/textarea/select element in the form
          const firstInput = document.querySelector(
            'form input:not([type="hidden"]):not([type="radio"]):not([type="checkbox"]):not([disabled]), form textarea:not([disabled]), form select:not([disabled])',
          ) as HTMLElement
          if (firstInput) {
            firstInput.focus()
          }
        }, 300)

        return () => clearTimeout(timerId)
      }
    }
    // No cleanup needed if conditions not met
    return undefined
  }, [subject, isLoading])

  const handleSubjectChange = (e: any) => {
    const value = Number(e.target.value) as FormId
    setSubject(value)

    applicationRequestClick(
      String(contactTypesData[value - 1]?.item2) || "",
      englishLabels[value - 1],
    )
    const url = new URL(window.location.href)
    if (value) {
      url.searchParams.set("form", value.toString())
    } else {
      url.searchParams.delete("form")
    }
    router.replace(url.pathname + url.search, { scroll: false })
  }

  return (
    <StyledLayout>
      {!isEmbedded && (
        <>
          <Typography variant="h4" id="demo-radio-buttons-group-label">
            {tInner("inquirySubject")}
          </Typography>

          <StyledRadioGroup
            aria-labelledby="demo-radio-buttons-group-label"
            defaultValue={subject}
            value={subject}
            onChange={handleSubjectChange}
            name="radio-buttons-group"
          >
            {[
              ...adjustedContactTypesData,
              // { item1: 7, item2: tInner("lostItem") }
            ].map((el, i) => (
              <StyledFormControlLabel
                key={i}
                value={el.item1}
                control={
                  <StyledRadio
                    disableRipple
                    icon={
                      <Image
                        width={24}
                        height={24}
                        src="/icons/radioEmpty.svg"
                        alt=""
                      />
                    }
                    checkedIcon={
                      <Image
                        width={24}
                        height={24}
                        src="/icons/radioFull.svg"
                        alt=""
                      />
                    }
                  />
                }
                label={hebrew ? el.item2 : englishLabels[i]}
              />
            ))}
          </StyledRadioGroup>
        </>
      )}
      {isLoading ? (
        <Loading />
      ) : (
        <>
          {subject === 1 && (
            <Complaint
              handleResult={handleResult}
              locale={locale}
              contactType={1}
              setIsLoading={setIsLoading}
              label={adjustedContactTypesData[0].item2}
              tooltipText={tooltipText}
            />
          )}
          {subject === 2 && (
            <OptimizationProposal
              handleResult={handleResult}
              locale={locale}
              contactType={2}
              setIsLoading={setIsLoading}
              label={adjustedContactTypesData[1].item2}
              tooltipText={tooltipText}
            />
          )}
          {subject === 3 && (
            <LostFound
              handleResult={handleResult}
              locale={locale}
              lostFoundContentData={lostFoundContentData}
              lostFoundColorsData={lostFoundColorsData}
              setIsLoading={setIsLoading}
              label={adjustedContactTypesData[2].item2}
              tooltipText={tooltipText}
            />
          )}
          {subject === 4 && (
            <Ravkav
              handleResult={handleResult}
              locale={locale}
              contactType={4}
              setIsLoading={setIsLoading}
              label={adjustedContactTypesData[3].item2}
              tooltipText={tooltipText}
            />
          )}
          {subject === 5 && (
            <PenaltyLookup
              locale={locale}
              label={adjustedContactTypesData[4].item2}
            />
          )}
          {subject === 6 && (
            <RequestForCredit
              handleResult={handleResult}
              locale={locale}
              contactType={6}
              setIsLoading={setIsLoading}
              label={adjustedContactTypesData[5].item2}
              tooltipText={tooltipText}
            />
          )}
          {subject === 7 && (
            <RequestForTaxInvoice
              handleResult={handleResult}
              locale={locale}
              contactType={7}
              label={adjustedContactTypesData[6].item2}
              setIsLoading={setIsLoading}
              tooltipText={tooltipText}
            />
          )}
          {subject === 8 && (
            <>
              <LostStatusCheck
                lostAndFoundStatus={lostAndFoundStatus}
                locale={locale}
                label={adjustedContactTypesData[7].item2}
              />
              <InformationBlocks data={contactUsData} />
            </>
          )}
        </>
      )}
    </StyledLayout>
  )
}
